import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { CameraControlRequest } from '@/lib/api/cameras-api'
import { m } from '@/lib/paraglide/messages'

export type PtzDirection = CameraControlRequest['huong']

/**
 * Watchdog: nếu người dùng giữ nút quá lâu (hoặc thiết bị treo giữa lúc giữ),
 * tự gửi lệnh dừng. Không có nó, mất mạng đúng lúc đang giữ là camera quay mãi
 * — tài liệu tích hợp yêu cầu LUÔN có cơ chế này ở phía client.
 */
export const PTZ_WATCHDOG_MS = 5000

type SendCommand = (command: CameraControlRequest) => Promise<unknown>

/**
 * Hợp đồng bấm-giữ của API PTZ: `bat_dau` khi nhấn, `dung` khi thả.
 *
 * Mọi đường ra đều phải gửi `dung` — thả chuột, kéo ra khỏi nút, huỷ cảm ứng,
 * nhả phím, cửa sổ mất focus, rời tab, đóng khung xem. Hook gom hết vào một
 * chỗ để không call-site nào quên.
 */
export function useCameraPtz(send: SendCommand) {
    const [holding, setHolding] = useState<PtzDirection | null>(null)
    // Ref song song với state: handler cần biết "đang giữ hướng nào" ngay lập
    // tức, không chờ render tiếp theo (bấm/thả nhanh hơn một lần render).
    const holdingRef = useRef<PtzDirection | null>(null)
    const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const sendRef = useRef(send)

    useEffect(() => {
        sendRef.current = send
    }, [send])

    const clearWatchdog = useCallback(() => {
        if (watchdogRef.current !== null) {
            clearTimeout(watchdogRef.current)
            watchdogRef.current = null
        }
    }, [])

    const stop = useCallback(() => {
        const direction = holdingRef.current
        if (!direction) return
        holdingRef.current = null
        setHolding(null)
        clearWatchdog()
        // Lỗi của lệnh dừng nuốt có chủ đích: camera đã được yêu cầu dừng, báo
        // lỗi lúc người dùng vừa thả nút chỉ gây hoang mang. Watchdog phía
        // camera (và lần bấm sau) là lớp bảo vệ còn lại.
        void Promise.resolve(
            sendRef.current({ huong: direction, hanh_dong: 'dung' }),
        ).catch(() => undefined)
    }, [clearWatchdog])

    const start = useCallback(
        (direction: PtzDirection) => {
            // Đang giữ rồi thì không gửi bat_dau lần hai (tài liệu: không spam
            // lệnh bắt đầu trong lúc giữ).
            if (holdingRef.current) return
            holdingRef.current = direction
            setHolding(direction)

            clearWatchdog()
            watchdogRef.current = setTimeout(stop, PTZ_WATCHDOG_MS)

            void Promise.resolve(
                sendRef.current({ huong: direction, hanh_dong: 'bat_dau' }),
            ).catch(() => {
                toast.error(m.grower_area_camera_ptz_error())
                // Vẫn gửi dừng: có thể camera đã nhận lệnh rồi mới lỗi mạng.
                stop()
            })
        },
        [clearWatchdog, stop],
    )

    // Mất focus cửa sổ / rời tab: người dùng không còn nhìn thấy camera, không
    // được để nó tiếp tục quay.
    useEffect(() => {
        const onLeave = () => stop()
        const onVisibility = () => {
            if (document.hidden) stop()
        }
        window.addEventListener('blur', onLeave)
        document.addEventListener('visibilitychange', onVisibility)
        return () => {
            window.removeEventListener('blur', onLeave)
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [stop])

    // Đóng khung xem (unmount) cũng là một đường ra.
    useEffect(() => stop, [stop])

    return { holding, start, stop }
}
